import { ReportContentItem } from './report-settings.service';

// CONTRACT: every surface that draws a report — the editor, the PDF export,
// Present and the share link — draws a place's cards in one order: its
// pictures, then its text blocks, then its charts. What sits nowhere on the
// page comes last: text, then charts, then pictures.
export const REPORT_COVER_ID = 'cover_page';

export interface ReportPlaced {
	after?: string;
}

export interface ReportSlot {
	place: string | null;
	position: number;
}

export function reportPlaces(sections: ReportContentItem[]): string[] {
	return [REPORT_COVER_ID, ...sections.map(section => section.id)];
}

export function placeOf(
	card: ReportPlaced,
	places: ReadonlySet<string>
): string | null {
	return card.after && places.has(card.after) ? card.after : null;
}

export function placedAfter<T extends ReportPlaced>(
	cards: T[],
	place: string,
	places: ReadonlySet<string>
): T[] {
	return places.has(place) ? cards.filter(card => card.after === place) : [];
}

export function placedAtEnd<T extends ReportPlaced>(
	cards: T[],
	places: ReadonlySet<string>
): T[] {
	return cards.filter(card => placeOf(card, places) === null);
}

export function placedGroups<T extends ReportPlaced>(
	cards: T[],
	places: readonly string[]
): { after: Record<string, T[]>; end: T[] } {
	const shown = new Set(places);
	const after: Record<string, T[]> = {};
	places.forEach(place => (after[place] = []));
	const end: T[] = [];
	cards.forEach(card => {
		const place = placeOf(card, shown);
		(place === null ? end : after[place]).push(card);
	});
	return { after, end };
}

// TRAP: a card already drawn at `place` comes back as the same object, `after`
// and all — an edit waiting on a chart finds it again by identity, and a card
// after a switched-off section keeps that section to go back to.
export function withPlace<T extends ReportPlaced>(
	card: T,
	place: string | null,
	places: ReadonlySet<string>
): T {
	if (placeOf(card, places) === place) {
		return card;
	}
	const placed: T = { ...card };
	if (place === null) {
		delete placed.after;
	} else {
		placed.after = place;
	}
	return placed;
}

function placeRank(place: string | null, places: readonly string[]): number {
	const rank = place === null ? -1 : places.indexOf(place);
	return rank === -1 ? places.length : rank;
}

export function placedIndex(
	cards: ReportPlaced[],
	place: string | null,
	position: number,
	places: readonly string[]
): number {
	const shown = new Set(places);
	const members = cards
		.map((card, index) => (placeOf(card, shown) === place ? index : -1))
		.filter(index => index >= 0);
	if (members.length) {
		const at = Number.isFinite(position)
			? Math.min(Math.max(Math.trunc(position), 0), members.length)
			: members.length;
		return at < members.length
			? members[at]
			: members[members.length - 1] + 1;
	}
	// TRAP: an empty place takes its card where the document draws it, not at
	// the end of the list, so a list kept in reading order stays in it.
	const rank = placeRank(place, places);
	const later = cards.findIndex(
		card => placeRank(placeOf(card, shown), places) > rank
	);
	return later === -1 ? cards.length : later;
}

export function insertPlaced<T extends ReportPlaced>(
	cards: T[],
	card: T,
	slot: ReportSlot,
	places: readonly string[]
): T[] {
	const next = [...cards];
	next.splice(
		placedIndex(cards, slot.place, slot.position, places),
		0,
		withPlace(card, slot.place, new Set(places))
	);
	return next;
}

export function placedMoveOrder(
	cards: ReportPlaced[],
	from: number,
	slot: ReportSlot,
	places: readonly string[]
): number[] {
	const rest = cards.map((_, index) => index).filter(index => index !== from);
	rest.splice(
		placedIndex(
			rest.map(index => cards[index]),
			slot.place,
			slot.position,
			places
		),
		0,
		from
	);
	return rest;
}

export function movePlaced<T extends ReportPlaced>(
	cards: T[],
	from: number,
	slot: ReportSlot,
	places: readonly string[]
): T[] {
	const shown = new Set(places);
	return placedMoveOrder(cards, from, slot, places).map(index =>
		index === from
			? withPlace(cards[index], slot.place, shown)
			: cards[index]
	);
}

export function documentOrder(
	cards: ReportPlaced[],
	places: readonly string[]
): number[] {
	const shown = new Set(places);
	const ranks = cards.map(card => placeRank(placeOf(card, shown), places));
	return cards
		.map((_, index) => index)
		.sort((left, right) => ranks[left] - ranks[right] || left - right);
}

export function documentSlot(
	cards: ReportPlaced[],
	from: number,
	position: number,
	places: readonly string[]
): ReportSlot {
	const shown = new Set(places);
	const order = documentOrder(cards, places);
	const current = order.indexOf(from);
	const others = order.filter(index => index !== from);
	const at = Number.isFinite(position)
		? Math.min(Math.max(Math.trunc(position), 0), others.length)
		: others.length;
	if (at === current || !others.length) {
		const place = placeOf(cards[from], shown);
		return {
			place,
			position: others.filter(
				index => index < from && placeOf(cards[index], shown) === place
			).length
		};
	}
	// TRAP: at the edge of a place a position means two places. The card joins
	// the place of the last card it passed, so one step down never sends it
	// past every section to the end of the report.
	const follows = at > current;
	const neighbour = follows ? others[at - 1] : others[at];
	const place = placeOf(cards[neighbour], shown);
	const index = others
		.filter(other => placeOf(cards[other], shown) === place)
		.indexOf(neighbour);
	return { place, position: follows ? index + 1 : index };
}

export function slotAfterCard(
	cards: ReportPlaced[],
	from: number,
	target: number,
	places: readonly string[]
): ReportSlot {
	const shown = new Set(places);
	const place = placeOf(cards[target], shown);
	const members = cards
		.map((_, index) => index)
		.filter(
			index => index !== from && placeOf(cards[index], shown) === place
		);
	return { place, position: members.indexOf(target) + 1 };
}
